import { zodResolver } from '@hookform/resolvers/zod';
import {
  configMgmtControllerCreateFeeComponentType,
  configMgmtControllerDeleteFeeComponentType,
  configMgmtControllerListFeeComponentTypes,
  configMgmtControllerUpdateFeeComponentType,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Coins, Link2, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AppLayout } from '../../../components/layout/app-layout';
import { PageHeader } from '../../../components/layout/page-header';
import {
  CardDataTable,
  type CardDataTableColumn,
  type CardDataTableRowDef,
} from '../../../components/shared/card-data-table';
import { StatCard } from '../../../components/shared/stat-card';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { usePermissions } from '../../../hooks/use-permissions';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';
import { useFeeComponentsStore } from '../../../stores/config/fee-components.store';

const feeComponentSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface FeeComponentType {
  id: string;
  name: string;
  code: string;
  description?: string | null;
  status: string;
  deletedAt?: string | null;
  _count?: { riskCategoryFees: number };
}

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/config/fee-component-types/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('config:view')) throw redirect({ to: '/dashboard' });
  },
  component: FeeComponentTypesPage,
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function FeeTypeAvatar({ name }: { name: string }) {
  const colors = [
    'from-emerald-500 to-teal-600',
    'from-amber-500 to-orange-600',
    'from-blue-500 to-indigo-600',
    'from-violet-500 to-purple-600',
    'from-sky-500 to-cyan-600',
    'from-rose-500 to-pink-600',
  ];
  const idx = (name.charCodeAt(0) + name.length) % colors.length;
  return (
    <div
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm',
        colors[idx],
      )}
    >
      <Coins size={18} />
    </div>
  );
}

function generatePreviewCode(name: string): string {
  return (
    name
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_|_$/g, '') || '…'
  );
}

// ── List columns ──────────────────────────────────────────────────────────────

const COLUMNS: CardDataTableColumn[] = [
  { id: 'feeType', header: 'Fee Component Type' },
  { id: 'code', header: 'Code' },
  { id: 'description', header: 'Description' },
  { id: 'riskUses', header: 'Risk Category Uses', align: 'center' },
  { id: 'status', header: 'Status', align: 'center' },
];

const GRID = 'minmax(0,1fr) 160px minmax(0,1fr) 160px 110px';

// ── Page ──────────────────────────────────────────────────────────────────────

function FeeComponentTypesPage() {
  const { can } = usePermissions();
  const qc = useQueryClient();
  const canEdit = can('config:edit');

  const {
    search,
    debounced,
    page,
    addOpen,
    editTarget: _editTarget,
    deleteConfirm: _deleteConfirm,
    deleteError,
    setSearch,
    setDebounced,
    setPage,
    setAddOpen,
    setEditTarget,
    setDeleteConfirm,
    setDeleteError,
  } = useFeeComponentsStore();
  const editTarget = _editTarget as FeeComponentType | null;
  const deleteConfirm = _deleteConfirm as FeeComponentType | null;
  const PAGE_SIZE = 10;
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const createForm = useForm<{ name: string; description?: string }>({
    resolver: zodResolver(feeComponentSchema),
    defaultValues: { name: '', description: '' },
  });
  const editForm = useForm<{ name: string; description?: string }>({
    resolver: zodResolver(feeComponentSchema),
    defaultValues: { name: '', description: '' },
  });

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debounced]);

  useEffect(() => {
    if (editTarget)
      editForm.reset({
        name: editTarget.name,
        description: (editTarget as { description?: string }).description ?? '',
      });
  }, [(editTarget as { id?: string })?.id]);

  const { data: feeTypes = [], isLoading } = useQuery<FeeComponentType[]>({
    queryKey: ['allFeeComponentTypes'],
    queryFn: () => configMgmtControllerListFeeComponentTypes() as Promise<FeeComponentType[]>,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['allFeeComponentTypes'] });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      configMgmtControllerCreateFeeComponentType({
        requestBody: { name: data.name.trim(), description: data.description?.trim() || undefined },
      }),
    onSuccess: () => {
      invalidate();
      setAddOpen(false);
      createForm.reset();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      configMgmtControllerUpdateFeeComponentType({
        typeId: editTarget!.id,
        requestBody: { name: data.name.trim(), description: data.description?.trim() || undefined },
      }),
    onSuccess: () => {
      invalidate();
      setEditTarget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => configMgmtControllerDeleteFeeComponentType({ typeId: id }),
    onSuccess: () => {
      invalidate();
      setDeleteConfirm(null);
      setDeleteError(null);
    },
    onError: async (err: unknown) => {
      try {
        const body = (await (err as { response?: Response }).response?.json()) as
          | { message?: string }
          | undefined;
        setDeleteError(body?.message ?? 'Delete failed');
      } catch {
        setDeleteError('Delete failed');
      }
    },
  });

  // Stats
  const totalTypes = feeTypes.length;
  const activeCount = feeTypes.filter((f) => f.status === 'ACTIVE').length;
  const inactiveCount = feeTypes.filter((f) => f.status !== 'ACTIVE').length;
  const totalUses = feeTypes.reduce((s, f) => s + (f._count?.riskCategoryFees ?? 0), 0);

  // Filter
  const filtered = feeTypes.filter((f) => {
    if (!debounced) return true;
    const q = debounced.toLowerCase();
    return f.name.toLowerCase().includes(q) || f.code.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <AppLayout>
      <PageHeader
        title="Fee Component Types"
        description="Master list of fee component types that can be assigned to risk categories."
        actions={
          canEdit ? (
            <Button variant="success" onClick={() => setAddOpen(true)}>
              <Plus size={16} />
              New Fee Component Type
            </Button>
          ) : null
        }
      />

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          icon={<Coins size={18} />}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-500"
          delta={`${activeCount} active · ${inactiveCount} inactive`}
          value={totalTypes}
          label="Total Types"
          loading={isLoading}
        />
        <StatCard
          icon={<Coins size={18} />}
          iconBg="bg-green-50"
          iconColor="text-green-500"
          delta="Currently enabled"
          value={activeCount}
          label="Active"
          loading={isLoading}
        />
        <StatCard
          icon={<Coins size={18} />}
          iconBg="bg-gray-100"
          iconColor="text-gray-400"
          delta="Soft deleted"
          value={inactiveCount}
          label="Inactive"
          loading={isLoading}
        />
        <StatCard
          icon={<Link2 size={18} />}
          iconBg="bg-violet-50"
          iconColor="text-violet-500"
          delta="Risk category assignments"
          value={totalUses}
          label="Total Uses"
          loading={isLoading}
        />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="relative min-w-60 flex-1">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or code…"
            className="h-9 w-full rounded-xl border border-gray-300 bg-gray-50 pl-10 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <span className="ml-auto text-xs text-gray-400">
          {filtered.length} of {totalTypes} types
        </span>
      </div>

      {/* List */}
      <CardDataTable
        gridTemplateColumns={GRID}
        columns={COLUMNS}
        isLoading={isLoading}
        emptyContent={
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center shadow-sm">
            <Coins size={40} className="mx-auto mb-4 text-gray-200" />
            <p className="text-sm font-semibold text-gray-500">No fee component types found</p>
            <p className="mt-1 text-xs text-gray-400">
              {search
                ? 'Try adjusting your search'
                : 'Add your first fee component type to get started'}
            </p>
          </div>
        }
        rows={paginated.map(
          (ft): CardDataTableRowDef => ({
            id: ft.id,
            avatar: <FeeTypeAvatar name={ft.name} />,
            cells: {
              feeType: (
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900 leading-snug">
                    {ft.name}
                  </p>
                </div>
              ),
              code: (
                <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs font-semibold text-gray-500">
                  {ft.code}
                </span>
              ),
              description: (
                <span className="text-xs text-gray-500 truncate">{ft.description ?? '—'}</span>
              ),
              riskUses: (
                <div className="flex items-center justify-center gap-1.5">
                  <Link2 size={12} className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">
                    {ft._count?.riskCategoryFees ?? 0}
                  </span>
                </div>
              ),
              status: (
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
                    ft.status === 'ACTIVE'
                      ? 'bg-success-50 text-success-700 border border-success-200'
                      : 'bg-gray-100 text-gray-500 border border-gray-200',
                  )}
                >
                  {ft.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                </span>
              ),
            },
            ...(canEdit
              ? {
                  actions: [
                    {
                      icon: <Pencil size={13} />,
                      label: 'Edit',
                      onClick: (e) => {
                        e.stopPropagation();
                        setEditTarget(ft);
                      },
                    },
                    {
                      icon: <Trash2 size={13} className="text-error-500" />,
                      label: 'Delete',
                      onClick: (e) => {
                        e.stopPropagation();
                        setDeleteConfirm(ft);
                        setDeleteError(null);
                      },
                    },
                  ],
                }
              : {}),
          }),
        )}
      />

      {/* Pagination */}
      {!isLoading && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {paginated.length} of {filtered.length} fee component types
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-colors',
                    page === p
                      ? 'bg-success-500 text-white shadow-sm'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
                  )}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create dialog */}
      <Dialog
        open={addOpen}
        onOpenChange={(v) => {
          if (!v) {
            setAddOpen(false);
            createForm.reset();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Fee Component Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={createForm.handleSubmit((d) => createMutation.mutate(d))}>
            <div className="space-y-4 py-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Name <span className="text-error-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Environmental Impact Fee"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all"
                  {...createForm.register('name')}
                />
                {createForm.formState.errors.name && (
                  <p className="mt-1 text-xs text-error-500">
                    {createForm.formState.errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Description
                </label>
                <input
                  type="text"
                  placeholder="Optional description"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all"
                  {...createForm.register('description')}
                />
              </div>
              {createForm.watch('name')?.trim() && (
                <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
                  <span className="text-xs text-gray-500">Code preview:</span>
                  <span className="font-mono text-xs font-bold text-gray-700">
                    {generatePreviewCode(createForm.watch('name'))}
                  </span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => {
                  setAddOpen(false);
                  createForm.reset();
                }}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button variant="success" size="sm" type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating…' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={!!editTarget}
        onOpenChange={(v) => {
          if (!v) setEditTarget(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Fee Component Type</DialogTitle>
          </DialogHeader>
          <form onSubmit={editForm.handleSubmit((d) => updateMutation.mutate(d))}>
            <div className="space-y-4 py-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Name <span className="text-error-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all"
                  {...editForm.register('name')}
                />
                {editForm.formState.errors.name && (
                  <p className="mt-1 text-xs text-error-500">
                    {editForm.formState.errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Description
                </label>
                <input
                  type="text"
                  placeholder="Optional description"
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all"
                  {...editForm.register('description')}
                />
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
                <span className="text-xs text-gray-500">Code (immutable):</span>
                <span className="font-mono text-xs font-bold text-gray-700">
                  {editTarget?.code}
                </span>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => setEditTarget(null)}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button variant="success" size="sm" type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog
        open={!!deleteConfirm}
        onOpenChange={(v) => {
          if (!v) {
            setDeleteConfirm(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Fee Component Type</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-gray-700">
              Delete <span className="font-semibold">{deleteConfirm?.name}</span>?
            </p>
            <p className="text-xs text-gray-500">
              This type is used in {deleteConfirm?._count?.riskCategoryFees ?? 0} risk category
              assignment(s). It will be deactivated and no longer selectable.
            </p>
            {deleteError && (
              <p className="rounded-xl border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-600">
                {deleteError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDeleteConfirm(null);
                setDeleteError(null);
              }}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => deleteMutation.mutate(deleteConfirm!.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
